import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { Download, FileText } from "lucide-react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import GoalQuestions from "../components/GoalQuestions_new";
import { useQuiz } from "../contexts/QuizContext";
import { toast } from "react-toastify";
import "../styles/components/SamplePaper.css";
import jsPDF from "jspdf";
import RateLimitModal from "../components/RateLimitModal";
import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";

const QUIZ_RATE_LIMIT_SUPPORT_MESSAGE =
  "You've reached the current limit for worksheet generation. Please wait a few minutes before trying again or contact support if you need a higher limit.";

// LaTeX rendering component
const LaTeXRenderer = ({ text }) => {
  const [rendered, setRendered] = useState(text);

  useEffect(() => {
    console.log("[SampleWorksheet] LaTeXRenderer useEffect triggered", text);
    if (text && typeof text === "string") {
      // Process LaTeX delimiters and mathematical expressions
      let processed = text
        // Handle LaTeX delimiters ($...$ for inline math)
        .replace(/\$([^$]+)\$/g, '<span class="inline-math">$1</span>')
        // Handle common LaTeX commands
        .replace(
          /\\frac\{([^}]+)\}\{([^}]+)\}/g,
          '<span class="inline-math">$1/$2</span>'
        )
        .replace(/\\sqrt\{([^}]+)\}/g, '<span class="inline-math">√$1</span>')
        .replace(/\\pi/g, '<span class="inline-math">π</span>')
        .replace(/\\alpha/g, '<span class="inline-math">α</span>')
        .replace(/\\beta/g, '<span class="inline-math">β</span>')
        .replace(/\\gamma/g, '<span class="inline-math">γ</span>')
        .replace(/\\delta/g, '<span class="inline-math">δ</span>')
        .replace(/\\theta/g, '<span class="inline-math">θ</span>')
        .replace(/\\lambda/g, '<span class="inline-math">λ</span>')
        .replace(/\\mu/g, '<span class="inline-math">μ</span>')
        .replace(/\\sigma/g, '<span class="inline-math">σ</span>')
        .replace(/\\phi/g, '<span class="inline-math">φ</span>')
        .replace(/\\omega/g, '<span class="inline-math">ω</span>')
        .replace(/\\sum/g, '<span class="inline-math">∑</span>')
        .replace(/\\int/g, '<span class="inline-math">∫</span>')
        .replace(/\\infty/g, '<span class="inline-math">∞</span>')
        .replace(/\\leq/g, '<span class="inline-math">≤</span>')
        .replace(/\\geq/g, '<span class="inline-math">≥</span>')
        .replace(/\\neq/g, '<span class="inline-math">≠</span>')
        .replace(/\\approx/g, '<span class="inline-math">≈</span>')
        .replace(/\\pm/g, '<span class="inline-math">±</span>')
        .replace(/\\times/g, '<span class="inline-math">×</span>')
        .replace(/\\div/g, '<span class="inline-math">÷</span>')
        .replace(/\\cdot/g, '<span class="inline-math">·</span>')
        .replace(/\\rightarrow/g, '<span class="inline-math">→</span>')
        .replace(/\\leftarrow/g, '<span class="inline-math">←</span>')
        .replace(/\\Rightarrow/g, '<span class="inline-math">⇒</span>')
        .replace(/\\Leftarrow/g, '<span class="inline-math">⇐</span>')
        .replace(/\\in/g, '<span class="inline-math">∈</span>')
        .replace(/\\notin/g, '<span class="inline-math">∉</span>')
        .replace(/\\subset/g, '<span class="inline-math">⊂</span>')
        .replace(/\\supset/g, '<span class="inline-math">⊃</span>')
        .replace(/\\cup/g, '<span class="inline-math">∪</span>')
        .replace(/\\cap/g, '<span class="inline-math">∩</span>')
        .replace(/\\emptyset/g, '<span class="inline-math">∅</span>')
        .replace(/\\forall/g, '<span class="inline-math">∀</span>')
        .replace(/\\exists/g, '<span class="inline-math">∃</span>')
        // Handle degree symbol
        .replace(/\\circ/g, '<span class="inline-math">°</span>')
        // Handle superscripts and subscripts (improved patterns)
        .replace(/\^(\d+)/g, "<sup>$1</sup>")
        .replace(/_(\d+)/g, "<sub>$1</sub>")
        .replace(/\^([a-zA-Z])/g, "<sup>$1</sup>")
        .replace(/_([a-zA-Z])/g, "<sub>$1</sub>")
        // Handle common mathematical patterns like x2 -> x²
        .replace(/([a-zA-Z])(\d+)/g, (match, letter, number) => {
          // Only convert if it's a single letter followed by a number (like x2, y3)
          if (letter.length === 1 && number.length <= 2) {
            return `${letter}<sup>${number}</sup>`;
          }
          return match;
        })
        // Handle specific patterns like 2x2 -> 2x²
        .replace(/(\d+)([a-zA-Z])(\d+)/g, (match, coeff, letter, power) => {
          if (letter.length === 1 && power.length === 1) {
            return `${coeff}${letter}<sup>${power}</sup>`;
          }
          return match;
        });

      setRendered(processed);
    }
  }, [text]);

  return <span dangerouslySetInnerHTML={{ __html: rendered }} />;
};

// Function to convert LaTeX to plain text for PDF
const latexToPlainText = (text) => {
  if (!text || typeof text !== "string") return text;

  console.log("latexToPlainText input:", text);

  // Test the regex pattern directly
  const testText = "2x2 + 7x + 3";
  const testMatch = testText.match(/([a-zA-Z])(\d+)/g);
  console.log("Test regex match:", testMatch);

  const result = text
    // Handle LaTeX delimiters ($...$ for inline math)
    .replace(/\$([^$]+)\$/g, "$1")
    // Handle common LaTeX commands
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "($1/$2)")
    .replace(/\\sqrt\{([^}]+)\}/g, "sqrt($1)")
    .replace(/\\pi/g, "pi")
    .replace(/\\alpha/g, "alpha")
    .replace(/\\beta/g, "beta")
    .replace(/\\gamma/g, "gamma")
    .replace(/\\delta/g, "delta")
    .replace(/\\theta/g, "theta")
    .replace(/\\lambda/g, "lambda")
    .replace(/\\mu/g, "mu")
    .replace(/\\sigma/g, "sigma")
    .replace(/\\phi/g, "phi")
    .replace(/\\omega/g, "omega")
    .replace(/\\sum/g, "sum")
    .replace(/\\int/g, "integral")
    .replace(/\\infty/g, "infinity")
    .replace(/\\leq/g, "<=")
    .replace(/\\geq/g, ">=")
    .replace(/\\neq/g, "!=")
    .replace(/\\approx/g, "~=")
    .replace(/\\pm/g, "+/-")
    .replace(/\\times/g, "x")
    .replace(/\\div/g, "/")
    .replace(/\\cdot/g, ".")
    .replace(/\\rightarrow/g, "->")
    .replace(/\\leftarrow/g, "<-")
    .replace(/\\Rightarrow/g, "=>")
    .replace(/\\Leftarrow/g, "<=")
    .replace(/\\in/g, "in")
    .replace(/\\notin/g, "not in")
    .replace(/\\subset/g, "subset")
    .replace(/\\supset/g, "superset")
    .replace(/\\cup/g, "union")
    .replace(/\\cap/g, "intersection")
    .replace(/\\emptyset/g, "empty set")
    .replace(/\\forall/g, "for all")
    .replace(/\\exists/g, "exists")
    // Handle degree symbol
    .replace(/\\circ/g, "°")
    // Handle superscripts and subscripts - convert ^2 to ², ^3 to ³, etc.
    .replace(/\^(\d+)/g, (match, number) => {
      console.log("Matched superscript pattern:", match, "number:", number);
      const superscriptMap = {
        0: "⁰",
        1: "¹",
        2: "²",
        3: "³",
        4: "⁴",
        5: "⁵",
        6: "⁶",
        7: "⁷",
        8: "⁸",
        9: "⁹",
      };
      const superscriptNumber = number
        .split("")
        .map((digit) => superscriptMap[digit] || digit)
        .join("");
      console.log("Converted superscript to:", superscriptNumber);
      return superscriptNumber;
    })
    .replace(/_(\d+)/g, "_$1")
    .replace(/\^([a-zA-Z])/g, "^$1")
    .replace(/_([a-zA-Z])/g, "_$1")
    // Handle common mathematical patterns like x2 -> x² (using proper superscript characters)
    .replace(/([a-zA-Z])(\d+)/g, (match, letter, number) => {
      console.log(
        "Matched pattern:",
        match,
        "letter:",
        letter,
        "number:",
        number
      );
      // Convert to proper superscript characters
      const superscriptMap = {
        0: "⁰",
        1: "¹",
        2: "²",
        3: "³",
        4: "⁴",
        5: "⁵",
        6: "⁶",
        7: "⁷",
        8: "⁸",
        9: "⁹",
      };
      const superscriptNumber = number
        .split("")
        .map((digit) => superscriptMap[digit] || digit)
        .join("");
      const result = `${letter}${superscriptNumber}`;
      console.log("Converted to:", result);
      return result;
    })
    // Handle specific patterns like 2x2 -> 2x²
    .replace(/(\d+)([a-zA-Z])(\d+)/g, (match, coeff, letter, power) => {
      const superscriptMap = {
        0: "⁰",
        1: "¹",
        2: "²",
        3: "³",
        4: "⁴",
        5: "⁵",
        6: "⁶",
        7: "⁷",
        8: "⁸",
        9: "⁹",
      };
      const superscriptPower = superscriptMap[power] || power;
      return `${coeff}${letter}${superscriptPower}`;
    });

  console.log("latexToPlainText output:", result);
  return result;
};

// Function to sanitize text for PDF to prevent encoding issues
const sanitizeForPDF = (text) => {
  if (!text || typeof text !== "string") return text;

  return (
    text
      // Keep superscript characters (Unicode superscripts)
      .replace(/[^\x00-\x7F⁰¹²³⁴⁵⁶⁷⁸⁹]/g, "") // Remove non-ASCII except superscripts
      .replace(/[^\w\s\.\,\!\?\;\:\-\(\)\[\]\{\}\+\-\*\/\=\<\>⁰¹²³⁴⁵⁶⁷⁸⁹]/g, "") // Keep safe characters + superscripts
      .trim()
  );
};

const QUESTION_PROMPT =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore";

const MCQ_OPTIONS = [
  { value: "A", label: "A. Lorem ipsum" },
  { value: "B", label: "B. Dolor sit amet" },
  { value: "C", label: "C. Consectetur adipiscing elit" },
  { value: "D", label: "D. Sed do eiusmod tempor incididunt ut labore" },
];

const PRACTICE_DURATION_OPTIONS = [15, 30, 45];
const FIXED_QUESTION_COUNT = 5;

const normalizeQueryParam = (value) => {
  if (typeof value !== "string") return "";
  const normalized = value.replace(/\+/g, " ").trim();
  if (!normalized) {
    return "";
  }
  const lowered = normalized.toLowerCase();
  if (lowered === "undefined" || lowered === "null") {
    return "";
  }
  return normalized;
};

const normalizeQuestionType = (type) => {
  if (!type) return "mcq";
  const normalized =
    typeof type === "string" ? type.trim().toLowerCase() : "mcq";
  switch (normalized) {
    case "short answer":
      return "short_answer";
    case "true/false":
    case "true false":
      return "true_false";
    case "mcq":
      return "mcq";
    default:
      return normalized.replace(/[^a-z0-9]+/g, "_") || "mcq";
  }
};

const buildPlaceholderQuestions = (count) => {
  const safeCount = Number.isFinite(Number(count))
    ? Math.max(Number(count), 1)
    : 1;
  return Array.from({ length: safeCount }, (_, i) => ({
    question_text: `Sample Question ${i + 1}: ${QUESTION_PROMPT}`,
    options: MCQ_OPTIONS.map((o) => ({
      option_text: o.label.replace(/^\w\.\s*/, ""),
      is_correct: false,
    })),
    correctAnswer: 1,
    explanation: "This is a placeholder explanation for the answer.",
  }));
};

const getNormalizedQuestionTypeFromQuestion = (question) => {
  if (!question || typeof question !== "object") {
    return "";
  }
  const rawType =
    question.question_type ||
    question.questionType ||
    question.type ||
    (typeof question.meta === "object" ? question.meta?.question_type : "");
  if (typeof rawType === "string" && rawType.trim()) {
    return normalizeQuestionType(rawType);
  }
  if (Array.isArray(question.options) && question.options.length > 0) {
    return "mcq";
  }
  return "";
};

const getQuestionTypeDisplayLabel = (typeKey = "") => {
  switch (typeKey) {
    case "mcq":
      return "Multiple Choice";
    case "short_answer":
      return "Descriptive";
    case "true_false":
      return "True / False";
    default:
      if (!typeKey) {
        return "";
      }
      return typeKey
        .split("_")
        .map((segment) =>
          segment.length > 0
            ? segment[0].toUpperCase() + segment.slice(1)
            : segment
        )
        .join(" ");
  }
};

const summarizeWorksheetQuestionTypes = (questions) => {
  if (!Array.isArray(questions) || !questions.length) {
    return "";
  }
  const uniqueTypes = Array.from(
    new Set(
      questions
        .map((question) => getNormalizedQuestionTypeFromQuestion(question))
        .filter(Boolean)
    )
  );
  if (!uniqueTypes.length) {
    return "";
  }
  if (uniqueTypes.length === 1) {
    return getQuestionTypeDisplayLabel(uniqueTypes[0]);
  }
  return "Mixed Question Types";
};

const isMCQQuestion = (question) =>
  getNormalizedQuestionTypeFromQuestion(question) === "mcq";
const isTrueFalseQuestion = (question) =>
  getNormalizedQuestionTypeFromQuestion(question) === "true_false";

const getOptionLabel = (index) => {
  if (!Number.isInteger(index) || index < 0) {
    return "";
  }
  let label = "";
  let current = index;
  do {
    label = String.fromCharCode(65 + (current % 26)) + label;
    current = Math.floor(current / 26) - 1;
  } while (current >= 0);
  return label;
};

/**
 * Main component for the Exam Preparation Test Yourself UI.
 */
function SampleWorksheetPage() {
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const questionType = "MCQ";
  const numQuestions = FIXED_QUESTION_COUNT;
  const [isGenerating, setIsGenerating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [downloadWorksheetMeta, setDownloadWorksheetMeta] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [isRateLimitModalOpen, setIsRateLimitModalOpen] = useState(false);
  const [rateLimitServerMessage, setRateLimitServerMessage] = useState("");
  const [practiceDuration, setPracticeDuration] = useState(15);
  const timerRef = useRef(null);
  const { saveQuizState, resetQuizState } = useQuiz();
  const navigate = useNavigate();
  const location = useLocation();
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const isMasteryTestFlow = useMemo(() => {
    const searchParams = new URLSearchParams(location.search || "");
    return (
      (searchParams.get("from") || "").trim().toLowerCase() === "masterytest"
    );
  }, [location.search]);
  const worksheetQuestionCount =
    downloadWorksheetMeta?.questionCount ?? generatedQuestions.length;
  const worksheetTypeLabel =
    downloadWorksheetMeta?.questionTypeLabel ||
    getQuestionTypeDisplayLabel(normalizeQuestionType(questionType));
  const worksheetTimerSeconds =
    downloadWorksheetMeta?.timer ??
    (Number.isFinite(Number(practiceDuration)) && Number(practiceDuration) > 0
      ? Number(practiceDuration) * 60
      : Math.max(worksheetQuestionCount * 60, 60));
  const worksheetTimerMinutes = Math.max(
    1,
    Math.round(worksheetTimerSeconds / 60)
  );

  // Check if we're on the nested generated route
  const isOnGeneratedRoute = location.pathname.endsWith("/generated");

  useEffect(() => {
    console.log(
      "[SampleWorksheet] Query param sync useEffect triggered",
      location.search
    );
    const searchParams = new URLSearchParams(location.search || "");
    const normalizedSubject = normalizeQueryParam(searchParams.get("subject"));
    const normalizedChapter = normalizeQueryParam(searchParams.get("chapter"));

    if (normalizedSubject) {
      setSelectedSubject(normalizedSubject);
    } else {
      setSelectedSubject("");
    }
    if (normalizedChapter) {
      setSelectedTopic(normalizedChapter);
    } else {
      setSelectedTopic("");
    }
  }, [location.search]);
  const showRateLimitModal = useCallback((message) => {
    setRateLimitServerMessage(message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE);
    setIsRateLimitModalOpen(true);
  }, []);
  const handleRateLimitModalClose = useCallback(() => {
    setIsRateLimitModalOpen(false);
    setRateLimitServerMessage("");
  }, []);

  // Handler for the main button click
  const openQuizModal = ({
    quizPayload,
    totalQuestions,
    timer,
    goalDetails,
  }) => {
    const questionList = Array.isArray(quizPayload?.questions)
      ? quizPayload.questions
      : Array.isArray(quizPayload)
      ? quizPayload
      : [];
    if (!questionList.length) {
      toast.error("No questions available to start the quiz.");
      return;
    }
    const safeTotal =
      Number.isFinite(Number(totalQuestions)) && Number(totalQuestions) > 0
        ? Number(totalQuestions)
        : typeof quizPayload?.question_count === "number" &&
          Number.isFinite(quizPayload.question_count)
        ? Math.max(quizPayload.question_count, questionList.length)
        : questionList.length || 1;
    const computedTimer =
      Number.isFinite(Number(timer)) && Number(timer) > 0
        ? Number(timer)
        : typeof quizPayload?.timer === "number" &&
          Number.isFinite(quizPayload.timer) &&
          quizPayload.timer > 0
        ? quizPayload.timer
        : safeTotal * 60;
    const userAnswersSeed = Array.from({ length: safeTotal }, () => null);

    const baseMetadata =
      quizPayload &&
      typeof quizPayload === "object" &&
      !Array.isArray(quizPayload)
        ? { ...quizPayload }
        : {};
    const storedQuestions = {
      ...baseMetadata,
      questions: questionList,
      question_count: safeTotal,
      timer:
        Number.isFinite(Number(baseMetadata.timer)) &&
        Number(baseMetadata.timer) > 0
          ? Number(baseMetadata.timer)
          : computedTimer,
      subject: baseMetadata.subject ?? goalDetails?.subject ?? selectedSubject,
      topic: baseMetadata.topic ?? goalDetails?.topic ?? selectedTopic,
    };

    saveQuizState({
      topicId: "sample-paper",
      questions: storedQuestions,
      goalDetails: goalDetails || {
        subject: selectedSubject,
        topic: selectedTopic,
      },
      currentQuestionIndex: 0,
      selectedOption: null,
      score: 0,
      showExplanation: false,
      quizCompleted: false,
      leveledUp: false,
      goalStatus: "",
      userAnswers: userAnswersSeed,
    });

    setSelectedAnswers(userAnswersSeed);
    setCurrentQuestionIndex(0);
    setRemainingTime(computedTimer);
    setIsModalOpen(true);
  };
  const handleGenerate = async (mode = "online") => {
    const normalizedSubject = selectedSubject?.trim();
    const normalizedTopic = selectedTopic?.trim();
    const durationSeconds =
      Number.isFinite(Number(practiceDuration)) && Number(practiceDuration) > 0
        ? Number(practiceDuration) * 60
        : numQuestions * 60;
    const isOnlineMode = mode === "online";

    if (!normalizedSubject || !normalizedTopic) {
      toast.error("Subject or chapter information is missing.");
      return;
    }
    if (!questionType) {
      toast.error("Please select a question type.");
      return;
    }
    const totalRequested = Number(numQuestions);
    if (!Number.isFinite(totalRequested) || totalRequested <= 0) {
      toast.error("Please choose a valid number of questions.");
      return;
    }
    if (!baseUrl) {
      toast.error("VITE_APP_BASE_URL is not configured.");
      const fallbackQuestions = buildPlaceholderQuestions(totalRequested);
      const fallbackPayload = {
        questions: fallbackQuestions,
        question_count: fallbackQuestions.length,
        timer: durationSeconds,
        subject: normalizedSubject,
        topic: normalizedTopic,
      };
      if (isOnlineMode) {
        openQuizModal({
          quizPayload: fallbackPayload,
          totalQuestions: fallbackPayload.question_count,
          timer: durationSeconds,
          goalDetails: { subject: normalizedSubject, topic: normalizedTopic },
        });
      } else {
        setGeneratedQuestions(fallbackQuestions);
        setDownloadWorksheetMeta(null);
        setIsPdfPreviewOpen(true);
      }
      return;
    }

    setIsGenerating(true);
    try {
      const timerForRequest =
        Number.isFinite(durationSeconds) && durationSeconds > 0
          ? durationSeconds
          : Math.max(totalRequested * 60, 60);
      const normalizedQuestionType = normalizeQuestionType(questionType);
      const requestUrl = `${baseUrl}/v2/quiz/${
        isOnlineMode ? "generate-new" : "generate"
      }`;
      // Determine practice mode based on masteryFlowActive flag in sessionStorage.
      // If masteryFlowActive is "true", this is NOT practice; otherwise it is practice.
      const masteryFlowActiveRaw =
        typeof window !== "undefined"
          ? window.sessionStorage.getItem("masteryFlowActive")
          : null;
      const masteryFlowActive =
        typeof masteryFlowActiveRaw === "string"
          ? masteryFlowActiveRaw.toLowerCase() === "true"
          : false;
      const isPractice = !masteryFlowActive;
      const requestBody = isOnlineMode
        ? {
            subject: normalizedSubject,
            chapter: normalizedTopic,
            num_questions: totalRequested,
            question_type: normalizedQuestionType,
            level: "developing",
            timer: timerForRequest,
            isPractice,
          }
        : {
            subject: normalizedSubject,
            chapter: normalizedTopic,
            num_questions: totalRequested,
            question_type: normalizedQuestionType,
            level: "Intermediate",
            subType: "adaptive",
            isPractice,
          };

      console.log("SamplePaper | Sending quiz generation request", {
        url: requestUrl,
        payload: requestBody,
      });

      const headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };

      if (isOnlineMode) {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error(
            "Authentication token is missing. Please log in to generate online quizzes."
          );
          return;
        }
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(requestUrl, {
        method: "POST",
        headers,
        redirect: "follow",
        body: JSON.stringify(requestBody),
      });

      const rateLimitMessage = await extractRateLimitMessageFromResponse(
        response
      );
      if (rateLimitMessage) {
        const normalizedMessage =
          rateLimitMessage?.trim() || RATE_LIMIT_FALLBACK_MESSAGE;
        toast.warn(normalizedMessage);
        showRateLimitModal(normalizedMessage);
        return;
      }

      const responseText = await response.text();

      console.log("SamplePaper | Received quiz generation response", {
        status: response.status,
        ok: response.ok,
        body: responseText,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
      }

      let parsedData = {};
      if (responseText) {
        try {
          parsedData = JSON.parse(responseText);
        } catch (parseError) {
          console.error("Failed to parse quiz generator response:", parseError);
          throw new Error(
            "Received invalid JSON from the quiz generator service."
          );
        }
      }

      const quizPayload =
        parsedData?.result && typeof parsedData.result === "object"
          ? parsedData.result
          : parsedData;
      const questionList = Array.isArray(quizPayload?.questions)
        ? quizPayload.questions
        : [];

      if (!questionList.length) {
        throw new Error("No Questions found.");
      }

      const totalQuestions =
        typeof quizPayload?.question_count === "number" &&
        Number.isFinite(quizPayload.question_count)
          ? Math.max(quizPayload.question_count, questionList.length)
          : questionList.length || totalRequested;

      const timerSeconds =
        Number.isFinite(durationSeconds) && durationSeconds > 0
          ? durationSeconds
          : totalQuestions * 60;

      if (isOnlineMode) {
        openQuizModal({
          quizPayload,
          totalQuestions,
          timer: timerSeconds,
          goalDetails: { subject: normalizedSubject, topic: normalizedTopic },
        });
      } else {
        setGeneratedQuestions(questionList);
        setDownloadWorksheetMeta(null);
        setIsPdfPreviewOpen(true);
      }
    } catch (error) {
      console.error("Failed to generate sample paper:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to generate sample paper."
      );
      // const fallbackQuestions = buildPlaceholderQuestions(totalRequested);
      // const fallbackPayload = {
      //   questions: fallbackQuestions,
      //   question_count: fallbackQuestions.length,
      //   timer: durationSeconds,
      //   subject: normalizedSubject,
      //   topic: normalizedTopic,
      // };

      // if (isOnlineMode) {
      //   openQuizModal({
      //     quizPayload: fallbackPayload,
      //     totalQuestions: fallbackPayload.question_count,
      //     timer: durationSeconds,
      //     goalDetails: { subject: normalizedSubject, topic: normalizedTopic },
      //   });
      // } else {
      //   setGeneratedQuestions(fallbackQuestions);
      //   setIsPdfPreviewOpen(true);
      // }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadWorksheet = async () => {
    const normalizedSubject = selectedSubject?.trim();
    const normalizedTopic = selectedTopic?.trim();

    if (!normalizedSubject || !normalizedTopic) {
      toast.error("Subject or chapter information is missing.");
      return;
    }
    if (!baseUrl) {
      toast.error("VITE_APP_BASE_URL is not configured.");
      return;
    }

    const timerSeconds =
      Number.isFinite(Number(practiceDuration)) && Number(practiceDuration) > 0
        ? Number(practiceDuration) * 60
        : 900;

    const requestUrl = `${baseUrl}/v2/quiz/generate-adaptive-worksheet`;
    const requestBody = {
      subject: normalizedSubject,
      chapter: normalizedTopic,
      timer: timerSeconds,
      isPractice: !isMasteryTestFlow,
    };

    console.log("SamplePaper | Generating adaptive worksheet", {
      url: requestUrl,
      payload: requestBody,
    });

    setIsGenerating(true);
    try {
      const response = await fetch(requestUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });
      const responseText = await response.text();

      console.log("SamplePaper | Adaptive worksheet response", {
        status: response.status,
        ok: response.ok,
        body: responseText,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${responseText}`);
      }

      let parsedData = {};
      if (responseText) {
        try {
          parsedData = JSON.parse(responseText);
        } catch (parseError) {
          console.error(
            "SamplePaper | Failed to parse adaptive worksheet response",
            parseError
          );
          throw new Error(
            "Received invalid JSON from the worksheet generator service."
          );
        }
      }

      const worksheetPayload =
        parsedData?.result && typeof parsedData.result === "object"
          ? parsedData.result
          : parsedData;
      console.log(
        "SamplePaper | Parsed adaptive worksheet payload",
        worksheetPayload
      );

      const worksheetQuestions = Array.isArray(worksheetPayload?.questions)
        ? worksheetPayload.questions
        : [];
      if (!worksheetQuestions.length) {
        throw new Error("No Questions found.");
      }

      setGeneratedQuestions(worksheetQuestions);
      const questionTypeLabel =
        summarizeWorksheetQuestionTypes(worksheetQuestions) ||
        getQuestionTypeDisplayLabel(normalizeQuestionType(questionType));

      const resolvedQuestionCount =
        typeof worksheetPayload?.question_count === "number" &&
        Number.isFinite(worksheetPayload.question_count)
          ? worksheetPayload.question_count
          : worksheetQuestions.length;
      const resolvedTimer =
        typeof worksheetPayload?.timer === "number" &&
        Number.isFinite(worksheetPayload.timer)
          ? worksheetPayload.timer
          : timerSeconds;

      setDownloadWorksheetMeta({
        questionCount: resolvedQuestionCount,
        timer: resolvedTimer,
        attemptId:
          worksheetPayload?.attempt_id ||
          worksheetPayload?.attemptId ||
          "worksheet-preview",
        questionTypeLabel,
      });
      setIsPdfPreviewOpen(true);
    } catch (error) {
      console.error("Failed to generate adaptive worksheet:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to generate adaptive worksheet."
      );
    } finally {
      setIsGenerating(false);
    }
  };

  useEffect(() => {
    console.log(
      "[SampleWorksheet] Practice timer useEffect triggered",
      isModalOpen
    );
    if (!isModalOpen) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      console.log("[SampleWorksheet] Practice timer useEffect cleanup");
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isModalOpen]);

  const closePracticeGame = () => {
    setIsModalOpen(false);
    setRemainingTime(0);
    setCurrentQuestionIndex(0);
    setSelectedAnswers([]);
    resetQuizState();
  };

  const generatePDF = () => {
    const pdf = new jsPDF();

    // Set encoding to handle special characters properly
    pdf.setFont("helvetica", "normal");

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    let yPosition = 20;
    const lineHeight = 7;
    const margin = 20;

    // Helper function to add Aanya watermark to current page
    const addWatermark = () => {
      // Save current graphics state
      pdf.saveGraphicsState();

      // Set watermark properties
      pdf.setFontSize(48);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(200, 200, 200); // Light gray color

      // Calculate center position for watermark
      const centerX = pageWidth / 2;
      const centerY = pageHeight / 2;

      // Add rotated watermark
      pdf.text("Aanya", centerX, centerY, {
        align: "center",
        angle: 45, // 45 degree rotation
      });

      // Restore graphics state
      pdf.restoreGraphicsState();
    };

    // Helper function to add text with word wrapping
    const addText = (text, fontSize = 12, isBold = false) => {
      pdf.setFontSize(fontSize);
      if (isBold) {
        pdf.setFont("helvetica", "bold");
      } else {
        pdf.setFont("helvetica", "normal");
      }

      const lines = pdf.splitTextToSize(text, pageWidth - 2 * margin);
      pdf.text(lines, margin, yPosition);
      yPosition += lines.length * lineHeight + 5;

      // Check if we need a new page
      if (yPosition > pageHeight - 20) {
        pdf.addPage();
        yPosition = 20;
        // Add watermark to new page
        addWatermark();
      }
    };

    // Header
    pdf.setFontSize(20);
    pdf.setFont("helvetica", "bold");
    pdf.text(`${selectedSubject} Worksheet`, pageWidth / 2, yPosition, {
      align: "center",
    });
    yPosition += 15;

    pdf.setFontSize(16);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Topic: ${selectedTopic}`, pageWidth / 2, yPosition, {
      align: "center",
    });
    yPosition += 10;

    pdf.setFontSize(12);
    pdf.text(
      `Questions: ${worksheetQuestionCount} • Type: ${
        worksheetTypeLabel || "Worksheet"
      } • Duration: ${worksheetTimerMinutes} mins • Difficulty: Intermediate`,
      pageWidth / 2,
      yPosition,
      { align: "center" }
    );
    yPosition += 20;

    // Add watermark to the first page
    addWatermark();

    // Helper function to get question difficulty and marks
    const getQuestionDifficulty = (question, index) => {
      // Check if question has difficulty property, otherwise assign based on index
      const difficulty =
        question.difficulty ||
        question.level ||
        (index < generatedQuestions.length / 3
          ? "easy"
          : index < (generatedQuestions.length * 2) / 3
          ? "medium"
          : "hard");
      return difficulty.toLowerCase();
    };

    const getQuestionMarks = (question, difficulty) => {
      // Assign marks based on difficulty
      const marksMap = { easy: 1, medium: 2, hard: 3 };
      return question.marks || marksMap[difficulty] || 2;
    };

    const getRoughWorkHeight = (difficulty) => {
      // Height in lines based on difficulty
      const heightMap = { easy: 3, medium: 6, hard: 9 };
      return heightMap[difficulty] || 6;
    };

    // Helper function to calculate total space needed for a question
    const calculateQuestionSpace = (question, index) => {
      const difficulty = getQuestionDifficulty(question, index);
      const roughWorkHeight = getRoughWorkHeight(difficulty);

      const questionText =
        question.questionText ||
        question.question_text ||
        question.question ||
        `Question ${index + 1}`;
      const plainText = latexToPlainText(questionText);
      const sanitizedText = sanitizeForPDF(plainText);

      // Calculate question text height
      const leftColumnWidth = pageWidth * 0.7;
      const questionLines = pdf.splitTextToSize(
        `${index + 1}. ${sanitizedText}`,
        leftColumnWidth - margin
      );
      const questionHeight = questionLines.length * lineHeight + 10;

      // Calculate options height
      let optionsHeight = 0;
      const mcqQuestion = isMCQQuestion(question);
      if (mcqQuestion && question.options && question.options.length > 0) {
        question.options.forEach((option, optionIndex) => {
          const optionText =
            typeof option === "string"
              ? option
              : option.option_text || option.text || option;
          const plainOptionText = latexToPlainText(optionText);
          const sanitizedOptionText = sanitizeForPDF(plainOptionText);
          const optionLabel = getOptionLabel(optionIndex);
          const optionPrefix = optionLabel ? `${optionLabel}.` : "";
          const optionLines = pdf.splitTextToSize(
            `   ${optionPrefix} ${sanitizedOptionText}`,
            pageWidth - 2 * margin
          );
          optionsHeight += optionLines.length * lineHeight + 2;
        });
      } else if (isTrueFalseQuestion(question)) {
        optionsHeight = 2 * lineHeight + 4; // A. True, B. False
      }

      // Calculate rough work height
      const roughWorkBoxHeight = roughWorkHeight * lineHeight;
      const roughWorkHeaderHeight = 15; // Header + spacing

      return (
        questionHeight +
        optionsHeight +
        roughWorkHeaderHeight +
        roughWorkBoxHeight +
        20
      ); // 20 for spacing
    };

    // Add questions
    generatedQuestions.forEach((question, index) => {
      const difficulty = getQuestionDifficulty(question, index);
      const marks = getQuestionMarks(question, difficulty);
      const roughWorkHeight = getRoughWorkHeight(difficulty);
      const mcqQuestion = isMCQQuestion(question);
      const trueFalseQuestion = isTrueFalseQuestion(question);

      // Check if we need a new page before adding this question
      const requiredSpace = calculateQuestionSpace(question, index);
      if (yPosition + requiredSpace > pageHeight - 20) {
        pdf.addPage();
        yPosition = 20;
        // Add watermark to new page
        addWatermark();
      }

      // Question number and text
      const questionText =
        question.questionText ||
        question.question_text ||
        question.question ||
        `Question ${index + 1}`;
      const plainText = latexToPlainText(questionText);
      const sanitizedText = sanitizeForPDF(plainText);

      // Split question into two columns: question text (left) and answer box (right)
      const leftColumnWidth = pageWidth * 0.7; // 70% for question
      const rightColumnWidth = pageWidth * 0.25; // 25% for answer box
      const rightColumnStart = pageWidth * 0.72; // Start position for right column

      // Add question text (left column)
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "normal");
      const questionLines = pdf.splitTextToSize(
        `${index + 1}. ${sanitizedText}`,
        leftColumnWidth - margin
      );
      pdf.text(questionLines, margin, yPosition);

      // Add answer box (right column) - just an empty square
      const answerBoxY = yPosition - 5; // Slightly above question text
      const answerBoxSize = 20; // Square box

      // Draw answer box border
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.rect(rightColumnStart, answerBoxY, answerBoxSize, answerBoxSize);

      // Add marks info on the right side below the answer box
      const marksText = `[${marks} mark${marks > 1 ? "s" : ""}]`;
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "italic");
      pdf.text(marksText, rightColumnStart, answerBoxY + answerBoxSize + 8);

      // Update yPosition to the end of question text
      yPosition += Math.max(
        questionLines.length * lineHeight + 10,
        answerBoxSize + 10
      );

      // Add options if they exist (without A/B/C/D labels since API already includes them)
      if (mcqQuestion && question.options && question.options.length > 0) {
        question.options.forEach((option, optionIndex) => {
          const optionText =
            typeof option === "string"
              ? option
              : option.option_text || option.text || option;
          const plainOptionText = latexToPlainText(optionText);
          const sanitizedOptionText = sanitizeForPDF(plainOptionText);
          const optionLabel = getOptionLabel(optionIndex);
          const optionPrefix = optionLabel ? `${optionLabel}.` : "";
          addText(`   ${optionPrefix} ${sanitizedOptionText}`, 10, false);
        });
      } else if (trueFalseQuestion) {
        addText("   A. True", 10, false);
        addText("   B. False", 10, false);
      }

      // Add rough work section
      yPosition += 5; // Small space before rough work section

      // Rough work section header
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "bold");
      pdf.text("Calculations / Step-by-step Solution:", margin, yPosition);
      yPosition += 5;

      // Draw rough work box (empty box without lines)
      const roughWorkBoxHeight = roughWorkHeight * lineHeight;
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.5);
      pdf.rect(margin, yPosition, pageWidth - 2 * margin, roughWorkBoxHeight);

      yPosition += roughWorkBoxHeight + 15; // Space between questions
    });

    // Footer
    yPosition = pageHeight - 20;
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.text(
      `Generated on ${new Date().toLocaleDateString()}`,
      pageWidth / 2,
      yPosition,
      { align: "center" }
    );

    // Download the PDF
    const fileName = `${selectedSubject}_${selectedTopic}_Worksheet_${
      new Date().toISOString().split("T")[0]
    }.pdf`;
    pdf.save(fileName);
    toast.success("Worksheet downloaded successfully!");
  };

  const rateLimitModal = (
    <RateLimitModal
      isOpen={isRateLimitModalOpen}
      serverMessage={rateLimitServerMessage}
      supportMessage={QUIZ_RATE_LIMIT_SUPPORT_MESSAGE}
      onClose={handleRateLimitModalClose}
    />
  );

  // If we're on the nested generated route, only render the outlet
  if (isOnGeneratedRoute) {
    return (
      <>
        <Outlet />
        {rateLimitModal}
      </>
    );
  }

  return (
    <>
      <div className="w-full mx-auto bg-white rounded-3xl  font-sans space-y-10">
        <div className="relative px-2 mb-0">
          <div className="absolute top-0 right-2 z-10">
            <button
              onClick={() =>
                navigate(
                  `/practice/sampleWorksheet/generated${location.search || ""}`
                )
              }
              className="px-4 py-2 rounded-lg text-[#0D99FF] text-sm font-medium bg-white hover:bg-[#0D99FF] hover:text-white transition-all duration-300 ease-in-out hover:shadow-md flex items-center gap-2"
            >
              <FileText size={16} />
              View Past Worksheets
            </button>
          </div>
          <div className="space-y-3 w-full">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0D99FF]">
              {/* Adaptive Practice Session */}
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-snug w-full text-center">
              Level up your mastery in{" "}
              <span className="text-[#0D99FF]">
                {selectedTopic || "this topic"}
              </span>
            </h1>
            {/* <p className="text-base text-gray-600 max-w-2xl">
              Questions difficulty level will be adapted based on your
              current mastery level so you always stay challenged and
              confident.
            </p> */}
            {(!selectedSubject || !selectedTopic) && (
              <p className="text-sm text-red-500">
                Subject and chapter are read from the URL (?subject=&chapter=).
                Add them to enable practice.
              </p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl px-6 py-6 text-center mt-6 mb-10">
          <p className="text-gray-800 font-semibold mb-4">
            How long would you like to practice?
          </p>
          <div className="flex flex-wrap justify-center gap-5">
            {PRACTICE_DURATION_OPTIONS.map((minutes) => {
              const isActive = Number(practiceDuration) === minutes;
              return (
                <button
                  key={minutes}
                  type="button"
                  onClick={() => setPracticeDuration(minutes)}
                  className={`w-32 h-14 rounded-full text-base font-semibold transition-all duration-200 ${
                    isActive
                      ? "bg-[#0D99FF] text-white shadow-lg shadow-[#0D99FF]/40"
                      : "bg-white border border-gray-200 text-gray-600 hover:border-[#0D99FF]"
                  }`}
                >
                  {minutes} Mins
                </button>
              );
            })}
          </div>
        </div>

        <div className="text-center space-y-4">
          <p className="text-gray-800 font-semibold">
            Choose your practice style:
          </p>
          <div className="flex flex-col gap-5 max-w-2xl mx-auto w-1/2 pb-10">
            <button
              type="button"
              onClick={() => handleGenerate("online")}
              disabled={isGenerating || !selectedSubject || !selectedTopic}
              className={`w-full md:flex-1 py-5 rounded-2xl text-white text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                isGenerating || !selectedSubject || !selectedTopic
                  ? "bg-[#0D99FF]/50 cursor-not-allowed"
                  : "bg-[#0D99FF] hover:bg-[#FF6B00] shadow-lg shadow-[#0D99FF]/40"
              }`}
            >
              {isGenerating
                ? "Preparing your session..."
                : `Start ${practiceDuration}-Minute Online Practice`}
            </button>
            <button
              type="button"
              onClick={handleDownloadWorksheet}
              disabled={isGenerating || !selectedSubject || !selectedTopic}
              className={`w-full md:flex-1 py-5 rounded-2xl text-lg font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
                isGenerating || !selectedSubject || !selectedTopic
                  ? "border border-gray-200 text-gray-400 cursor-not-allowed bg-white"
                  : "border-2 border-[#0D99FF] text-[#0D99FF] bg-white hover:bg-[#0D99FF]/5"
              }`}
            >
              <Download className="w-5 h-5" />
              {isGenerating
                ? "Preparing download..."
                : `Download ${practiceDuration}-Minute Worksheet`}
            </button>
          </div>
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black bg-opacity-50"
              aria-hidden="true"
            />
            <div className="relative w-[100vw] h-[98vh] max-w-none rounded-2xl bg-white p-0 shadow-xl overflow-hidden">
              <div className="h-full">
                <GoalQuestions embedded onClose={closePracticeGame} />
              </div>
            </div>
          </div>
        )}

        {/* PDF Preview Modal */}
        {isPdfPreviewOpen && (
          <>
            <style jsx>{`
              .inline-math {
                font-family: "Times New Roman", serif;
                font-style: italic;
                font-weight: 500;
                color: #1f2937;
              }
              .inline-math sup {
                font-size: 0.8em;
                vertical-align: super;
              }
              .inline-math sub {
                font-size: 0.8em;
                vertical-align: sub;
              }
            `}</style>
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div
                className="absolute inset-0 bg-black bg-opacity-50"
                aria-hidden="true"
              />
              <div className="relative w-[98vw] h-[98vh] max-w-none rounded-2xl bg-white p-0 shadow-xl overflow-hidden">
                <div className="h-full flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        Worksheet Preview - {selectedSubject} - {selectedTopic}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {worksheetQuestionCount} questions •{" "}
                        {worksheetTypeLabel || "Worksheet"} •{" "}
                        {worksheetTimerMinutes}-Minute Session
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={generatePDF}
                        className="px-4 py-2 bg-[#0D99FF] text-white rounded-lg hover:bg-[#0D99FF]/90 transition-colors text-sm font-medium flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download PDF
                      </button>
                      <button
                        onClick={() => setIsPdfPreviewOpen(false)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  {/* PDF Content */}
                  <div className="flex-1 overflow-auto p-6 bg-white">
                    <div className="max-w-4xl mx-auto">
                      <div className="mb-8 text-center">
                        <h1 className="text-2xl font-bold text-gray-900 mb-2">
                          {selectedSubject} Worksheet
                        </h1>
                        <h2 className="text-xl text-gray-700 mb-4">
                          Topic: {selectedTopic}
                        </h2>
                        <div className="flex justify-center gap-4 text-sm text-gray-600">
                          <span>Questions: {worksheetQuestionCount}</span>
                          <span>•</span>
                          <span>Type: {worksheetTypeLabel || "Worksheet"}</span>
                          <span>•</span>
                          <span>Duration: {worksheetTimerMinutes} mins</span>
                        </div>
                      </div>

                      <div className="space-y-8">
                        {generatedQuestions.map((question, index) => {
                          const mcqQuestion = isMCQQuestion(question);
                          const trueFalseQuestion =
                            isTrueFalseQuestion(question);
                          const hasOptions =
                            Array.isArray(question.options) &&
                            question.options.length > 0;
                          const shouldShowOptions = mcqQuestion && hasOptions;

                          return (
                            <div
                              key={index}
                              className="border border-gray-200 rounded-lg p-6 bg-gray-50"
                            >
                              <div className="flex items-start gap-3 mb-4">
                                <span className="flex-shrink-0 w-8 h-8 bg-[#0D99FF] text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                  {index + 1}
                                </span>
                                <div className="flex-1">
                                  <p className="text-gray-900 font-medium leading-relaxed">
                                    <LaTeXRenderer
                                      text={
                                        question.questionText ||
                                        question.question_text ||
                                        question.question ||
                                        `Question ${index + 1}`
                                      }
                                    />
                                  </p>
                                </div>
                              </div>

                              {shouldShowOptions && (
                                <div className="ml-11 space-y-2">
                                  {question.options.map(
                                    (option, optionIndex) => {
                                      const optionLabel =
                                        getOptionLabel(optionIndex);
                                      return (
                                        <div
                                          key={optionIndex}
                                          className="flex items-start gap-3 p-2"
                                        >
                                          <span className="text-sm font-semibold text-gray-700">
                                            {optionLabel
                                              ? `${optionLabel}.`
                                              : ""}
                                          </span>
                                          <span className="text-gray-700 flex-1">
                                            <LaTeXRenderer
                                              text={
                                                typeof option === "string"
                                                  ? option
                                                  : option.option_text ||
                                                    option.text ||
                                                    option
                                              }
                                            />
                                          </span>
                                        </div>
                                      );
                                    }
                                  )}
                                </div>
                              )}

                              {trueFalseQuestion && !shouldShowOptions && (
                                <div className="ml-11 space-y-2">
                                  <div className="flex items-center gap-3 p-2">
                                    <span className="w-4 h-4 border-2 border-gray-400 rounded-full flex items-center justify-center">
                                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                                    </span>
                                    <span className="text-gray-700">True</span>
                                  </div>
                                  <div className="flex items-center gap-3 p-2">
                                    <span className="w-4 h-4 border-2 border-gray-400 rounded-full flex items-center justify-center">
                                      <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                                    </span>
                                    <span className="text-gray-700">False</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <div className="mt-12 text-center text-gray-500 text-sm">
                        <p>Generated on {new Date().toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Render nested routes */}
        <Outlet />
      </div>
      {rateLimitModal}
    </>
  );
}

export default SampleWorksheetPage;
